/**
 * The error contract, Docs/05 § 6.
 *
 * Every RPC and Edge Function returns failures in one shape:
 *
 *   { "error": { "code": "PLAN_LIMIT_EXCEEDED", "message": "…",
 *                "details": { "limit": 30, "used": 30 } } }
 *
 * The client maps a CODE to a localized message and NEVER shows the server's
 * `message` to a user. The reason is not politeness: the server string is
 * English, untranslated, sometimes a Postgres error, and occasionally leaks a
 * constraint name or a row id. It is for the developer and for Sentry.
 *
 * The i18n key for a code is `errors.<CODE>` — see `errorTranslationKey()`.
 */

/**
 * Declared as a frozen object rather than a TypeScript `enum`: an enum is a
 * runtime construct that does not tree-shake, cannot be iterated as values
 * without a helper, and produces a nominal type that resists a plain string
 * arriving over the wire. This gives the same autocomplete with none of that.
 */
export const ERROR_CODES = {
  // ── Authentication and identity ───────────────────────────────────────────
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  PHONE_NOT_VERIFIED: 'PHONE_NOT_VERIFIED',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',

  // ── Authorisation ─────────────────────────────────────────────────────────
  FORBIDDEN: 'FORBIDDEN',
  /** RLS refused the row. Indistinguishable from "not found" on purpose. */
  NOT_A_MEMBER: 'NOT_A_MEMBER',
  MISSING_CAPABILITY: 'MISSING_CAPABILITY',
  ADMIN_ONLY: 'ADMIN_ONLY',
  BLOCKED_BY_USER: 'BLOCKED_BY_USER',

  // ── Validation ────────────────────────────────────────────────────────────
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_ORG_NUMBER: 'INVALID_ORG_NUMBER',
  INVALID_PHONE: 'INVALID_PHONE',
  INVALID_DATE_RANGE: 'INVALID_DATE_RANGE',
  INVALID_PRICE: 'INVALID_PRICE',
  INVALID_VAT_RATE: 'INVALID_VAT_RATE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',

  // ── Resources ─────────────────────────────────────────────────────────────
  NOT_FOUND: 'NOT_FOUND',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  SLUG_TAKEN: 'SLUG_TAKEN',
  ORG_NUMBER_TAKEN: 'ORG_NUMBER_TAKEN',
  GONE: 'GONE',

  // ── The connected loop ────────────────────────────────────────────────────
  REQUEST_CLOSED: 'REQUEST_CLOSED',
  REQUEST_ALREADY_AWARDED: 'REQUEST_ALREADY_AWARDED',
  OFFER_ALREADY_SENT: 'OFFER_ALREADY_SENT',
  OFFER_EXPIRED: 'OFFER_EXPIRED',
  OFFER_WITHDRAWN: 'OFFER_WITHDRAWN',
  CANNOT_OFFER_ON_OWN_TENDER: 'CANNOT_OFFER_ON_OWN_TENDER',
  JOB_NOT_COMPLETE: 'JOB_NOT_COMPLETE',
  /** Completion is two-sided; one party alone cannot finish a job. */
  AWAITING_COUNTERPARTY: 'AWAITING_COUNTERPARTY',
  REVIEW_NOT_ELIGIBLE: 'REVIEW_NOT_ELIGIBLE',
  ALREADY_REVIEWED: 'ALREADY_REVIEWED',

  // ── Booking ───────────────────────────────────────────────────────────────
  SLOT_UNAVAILABLE: 'SLOT_UNAVAILABLE',
  DOUBLE_BOOKING: 'DOUBLE_BOOKING',
  BOOKING_TOO_SOON: 'BOOKING_TOO_SOON',
  BOOKING_CANCELLED: 'BOOKING_CANCELLED',
  OUTSIDE_OPENING_HOURS: 'OUTSIDE_OPENING_HOURS',

  // ── Business rules and limits ─────────────────────────────────────────────
  PLAN_LIMIT_EXCEEDED: 'PLAN_LIMIT_EXCEEDED',
  RATE_LIMITED: 'RATE_LIMITED',
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  BUSINESS_NOT_VERIFIED: 'BUSINESS_NOT_VERIFIED',
  BUSINESS_NOT_ACTIVE: 'BUSINESS_NOT_ACTIVE',
  LAST_OWNER: 'LAST_OWNER',

  // ── Payment ───────────────────────────────────────────────────────────────
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
  REFUND_FAILED: 'REFUND_FAILED',
  BID_OUTBID: 'BID_OUTBID',
  BIDDING_CLOSED: 'BIDDING_CLOSED',

  // ── Platform ──────────────────────────────────────────────────────────────
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  UPSTREAM_ERROR: 'UPSTREAM_ERROR',
  MAINTENANCE_MODE: 'MAINTENANCE_MODE',
  APP_UPDATE_REQUIRED: 'APP_UPDATE_REQUIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

export const ERROR_CODE_LIST = Object.values(ERROR_CODES) as readonly ErrorCode[]

/** Structured context for the message — never rendered raw, only interpolated. */
export type ErrorDetails = Record<string, unknown>

export interface AvexaErrorBody {
  code: ErrorCode
  /** Developer-facing. English, unstable, and safe to log — not to display. */
  message: string
  details?: ErrorDetails
}

export interface AvexaErrorResponse {
  error: AvexaErrorBody
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODE_LIST as readonly string[]).includes(value)
}

/**
 * Narrows an unknown caught value to the contract shape. Written defensively
 * because a `catch` block receives whatever was thrown: a PostgREST error, a
 * fetch `TypeError`, a string, or — on a bad day — `undefined`.
 */
export function isAvexaErrorResponse(value: unknown): value is AvexaErrorResponse {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false
  const error = (value as { error: unknown }).error
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    isErrorCode((error as { code: unknown }).code)
  )
}

/**
 * The code to act on for any thrown value. Falls back to INTERNAL_ERROR, which
 * is the honest answer: we do not know what happened, and the user should see
 * one generic message rather than a stack trace.
 */
export function getErrorCode(value: unknown): ErrorCode {
  if (isAvexaErrorResponse(value)) return value.error.code
  if (isErrorCode(value)) return value
  if (typeof value === 'object' && value !== null && 'code' in value) {
    const code = (value as { code: unknown }).code
    if (isErrorCode(code)) return code
  }
  return ERROR_CODES.INTERNAL_ERROR
}

/**
 * i18n key for a code: `errors.PLAN_LIMIT_EXCEEDED`. Both catalogues must carry
 * every code — a missing one shows the key itself, which is worse than a generic
 * apology, so the missing-key reporter in `@avexa/i18n` watches this namespace.
 */
export function errorTranslationKey(code: ErrorCode): `errors.${ErrorCode}` {
  return `errors.${code}`
}

/**
 * Codes where retrying the identical request can succeed. Everything else is a
 * decision the server has already made, and a retry button on it is a lie.
 */
export const RETRYABLE_ERROR_CODES: readonly ErrorCode[] = [
  ERROR_CODES.INTERNAL_ERROR,
  ERROR_CODES.SERVICE_UNAVAILABLE,
  ERROR_CODES.UPSTREAM_ERROR,
  ERROR_CODES.NETWORK_ERROR,
  ERROR_CODES.RATE_LIMITED,
]

export function isRetryable(code: ErrorCode): boolean {
  return RETRYABLE_ERROR_CODES.includes(code)
}

/**
 * Codes that mean "the session is no longer usable", so the client signs out and
 * routes to login rather than showing an error the user cannot act on.
 */
export const SESSION_ENDING_ERROR_CODES: readonly ErrorCode[] = [
  ERROR_CODES.UNAUTHENTICATED,
  ERROR_CODES.SESSION_EXPIRED,
  ERROR_CODES.ACCOUNT_SUSPENDED,
  ERROR_CODES.ACCOUNT_DELETED,
]

/** Builds the contract shape, for an Edge Function's failure path. */
export function toErrorResponse(
  code: ErrorCode,
  message: string,
  details?: ErrorDetails,
): AvexaErrorResponse {
  return { error: details === undefined ? { code, message } : { code, message, details } }
}
